<!-- Main Content Container -->
<div class="flex flex-col min-h-0">
    <section class="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
        <div class="p-6 md:p-8">
            <h1 class="pb-4 text-2xl font-bold border-b border-gray-200 md:text-3xl">
                {{ type }} Public Profile Management
            </h1>

            @if (loading) {
                <div class="flex items-center justify-center py-10">
                    <div class="text-center">
                        <lucide-icon [img]="Loader" class="w-8 h-8 mx-auto mb-3 text-primary animate-spin" />
                        <p class="text-sm text-gray-600">Loading Public Profile</p>
                    </div>
                </div>
            } @else {
                <div class="grid items-center grid-cols-1 gap-3 pt-6 md:grid-cols-[11rem_minmax(0,1fr)] md:gap-8">
                    <label for="code" class="text-sm font-semibold text-gray-800">
                        {{ type == "Investee" ? type + " Code" : "Corporate Name" }}
                    </label>

                    <div class="w-full md:max-w-md">
                        <app-input
                            id="code"
                            class="w-full"
                            [inputClass]="'input input--primary w-full'"
                            placeholder="{{ type }} id"
                            [value]="code"
                            [readonly]="true"
                        >
                        </app-input>
                    </div>
                </div>

                <div class="grid items-start grid-cols-1 gap-3 pt-6 mt-6 border-t border-gray-200 md:grid-cols-[11rem_minmax(0,1fr)] md:gap-8">
                    <label class="text-sm font-semibold text-gray-800 md:pt-2">Public Profile</label>

                    <div class="min-w-0">
                        <form [formGroup]="form">
                            <app-quill-editor
                                formControlName="content"
                                [placeholder]="''"
                                [maxLength]="500"
                                containerClass="border-primary text-black border-2 transition-all duration-200"
                            ></app-quill-editor>
                        </form>

                        @if (isEditing) {
                            <label class="flex items-start w-full gap-3 mt-4 cursor-pointer">
                                <input
                                    type="checkbox"
                                    [(ngModel)]="agreed"
                                    [disabled]="isSubmitting"
                                    class="w-5 h-5 mt-0.5 border-gray-300 rounded text-primary focus:ring-primary"
                                />
                                <app-safe-rich-text
                                    class="flex-1 min-w-0 text-sm"
                                    [html]="profileAcknowledgement"
                                    [emphasizeLinks]="true"
                                    (click)="onProfileAcknowledgementClick($event)"
                                ></app-safe-rich-text>
                            </label>
                        }
                    </div>
                </div>
            }
        </div>

        @if (!loading) {
            <div class="flex flex-col gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 md:flex-row md:items-center md:justify-between md:px-8">
                <p *ngIf="lastUpdated && hasNarrative" class="text-sm font-normal text-gray-700">
                    Profile Last Updated: <strong class="font-semibold text-gray-900">{{ lastUpdated }}</strong>
                </p>

                <div class="flex flex-col gap-2 md:flex-row md:justify-end md:ml-auto">
                    <app-button
                        *ngIf="!isEditing"
                        type="button"
                        [icon]="SquarePen"
                        iconPosition="left"
                        class="w-full md:w-auto"
                        btnClass="w-full md:w-auto button button--primary transition font-medium rounded-lg text-sm px-5 py-2.5 text-center flex items-center justify-center hover:shadow-md whitespace-nowrap"
                        [label]="hasNarrative ? 'Update Public Profile' : 'Create Public Profile'"
                        (click)="startEditing()"
                    ></app-button>

                    @if (isEditing) {
                        <button
                            type="button"
                            class="px-5 py-2.5 text-white button button--error rounded hover:button--error-80"
                            (click)="updateNarrative('cancel')"
                            [disabled]="isSubmitting"
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            class="px-5 py-2.5 button button--secondary text-black rounded hover:button--secondary-80"
                            (click)="updateNarrative('draft')"
                            [disabled]="!agreed || isSubmitting"
                        >
                            Save as Draft
                        </button>

                        <button
                            type="button"
                            class="px-5 py-2.5 text-white button button--success rounded hover:button--success-80"
                            (click)="updateNarrative('final')"
                            [disabled]="!agreed || isSubmitting"
                        >
                            Submit
                        </button>
                    }
                </div>
            </div>
        }
    </section>
</div>

<ng-template
    #updateProfileModal
    let-description="description"
    let-onConfirmRequest="onConfirmRequest"
    let-onClose="onClose"
>
    <div class="w-full max-w-xl">
        <p class="mb-6 text-gray-700" [innerHTML]="description"></p>
        <div class="flex justify-end space-x-2">
            <button
                type="button"
                class="px-5 py-2.5 text-sm font-semibold text-primary border border-primary rounded hover:bg-gray-100"
                (click)="onClose()"
            >
                Profile is up to date
            </button>
            <button
                type="button"
                class="px-5 py-2.5 text-sm font-semibold text-white bg-tertiary border border-transparent rounded hover:bg-red-700 flex items-center justify-center"
                (click)="onConfirmRequest()"
            >
                Update Public Profile
            </button>
        </div>
    </div>
</ng-template>

<ng-template #cancelModalDetails let-onCancel="onCancel">
    <div class="w-full max-w-md">
        <p class="mb-6 text-gray-700">
            Are you sure you want to cancel updating your public profile? All unsaved changes will be lost.
        </p>
        <div class="flex justify-end space-x-2">
            <button
                type="button"
                class="px-5 py-2.5 text-sm font-semibold text-primary border border-primary rounded hover:bg-gray-100"
                data-cancel
            >
                No, Go Back
            </button>
            <button
                type="button"
                class="px-5 py-2.5 text-sm font-semibold text-white bg-tertiary border border-transparent rounded hover:bg-tertiary-80 flex items-center justify-center"
                (click)="onCancel()"
            >
                Yes, Cancel
            </button>
        </div>
    </div>
</ng-template>

<ng-template #confirmModalDetails let-onConfirm="onConfirm" let-isDraft="isDraft">
    <div class="w-full max-w-md">
        <p class="mb-6 text-gray-700">
            {{
                isDraft
                    ? "Are you sure you want to save your public profile as a draft?"
                    : "Are you sure you want to submit your public profile?"
            }}
        </p>
        <div class="flex justify-end space-x-2">
            <button
                type="button"
                class="px-5 py-2.5 text-sm font-semibold text-primary border border-primary rounded hover:bg-gray-100"
                data-cancel
            >
                Cancel
            </button>
            <app-button
                type="button"
                [label]="isDraft ? 'Yes, Save as Draft' : 'Yes, Submit'"
                [loadingText]="isDraft ? 'Saving...' : 'Submitting...'"
                [isLoading]="isSubmitting"
                [btnClass]="
                    'px-5 py-2.5 text-sm font-semibold border border-transparent rounded flex items-center justify-center ' +
                    (isDraft
                        ? 'text-white bg-secondary hover:bg-secondary-80'
                        : 'text-white bg-green-600 hover:bg-green-700')
                "
                (click)="onConfirm()"
            >
            </app-button>
        </div>
    </div>
</ng-template>
